import { Box, Flex, Text, Title, createStyles } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MdKeyboardArrowLeft } from "react-icons/md";
import { LuSettings2, LuPlusSquare } from "react-icons/lu";
import swal from "sweetalert";
import { NewAddButton } from "../../../components/AddButton/NewAddButton";
import ActivityLibraryModal from "../../../components/ActivityLibraryModal";
import ActivityLibraryTable from "../../../components/ActivityLibraryTable";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import SupportMethodWizard from "../../../components/QuickNotesPicker/SupportMethodWizard";
import TableLoader from "../../../components/TableLoader";
import { deleteActivityLibraryItem, getActivityLibrary, toggleActivityLibraryStatus } from "../../../services/activityLibrary";
import { getDefaultPrompting, saveDefaultPrompting } from "../../../services/defaultPrompting";


const useStyles = createStyles(() => ({
  bgColorClass: {
    width: "100%",
    backgroundColor: "var(--app-bg)",
  },
  view: {
    padding: "8px 28px 28px",
  },
  backrow: {
    margin: "6px 0 2px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "14px 0 12px",
  },
  table: {
    margin: 0,
  },
}));

const ActivityLibrary = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.profile);
  const { activities, loading, defaultSupports } = useSelector((state) => state.activityLibrary);
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [defaultPromptOpen, { open: openDefaultPrompt, close: closeDefaultPrompt }] = useDisclosure(false);

  useEffect(() => {
    getActivityLibrary();
    getDefaultPrompting();
  }, []);

  const handleAdd = () => {
    setSelectedRow(null);
    open();
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    open();
  };

  const handleClose = () => {
    close();
    setSelectedRow(null);
  };

  const handleToggle = (row, active) => {
    toggleActivityLibraryStatus(row.id, active);
  };

  const handleDelete = async (row) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: `Delete "${row.name}"? Activities already used in past notes are not affected.`,
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) deleteActivityLibraryItem(row.id);
  };

  return (
    <Box className={classes.bgColorClass}>
      {opened && <ActivityLibraryModal opened={opened} close={handleClose} data={selectedRow} />}
      <AppNavbar title="Quick Notes" profile={profile} />
      <Box className={classes.view}>
        <Flex
          align="center"
          gap={6}
          className={classes.backrow}
          onClick={() => navigate("/home/notetype")}
          style={{ cursor: "pointer", width: "fit-content" }}
        >
          <MdKeyboardArrowLeft size={22} />
          <Text fw={400} fz={15} c={"var(--app-text)"} ff={"Inter"}>
            Back
          </Text>
        </Flex>
        <Box className={classes.header}>
          <Title order={3}>Quick Notes</Title>
          <Flex gap={10} align="center">
            <NewAddButton
              title="Set default prompting"
              icon={<LuSettings2 size={17} color="var(--app-text)" />}
              onClick={openDefaultPrompt}
              color="var(--app-surface)"
              textColor="var(--app-text)"
              variant="outline"
              borderColor="var(--app-border)"
            />
            <NewAddButton
              title="Add Quick Note Item"
              icon={<LuPlusSquare size={17} color="var(--app-on-primary)" />}
              onClick={handleAdd}
              color="var(--app-primary)"
              textColor="var(--app-on-primary)"
              variant="filled"
              borderColor="none"
            />
          </Flex>
        </Box>

        <Box className={classes.table}>
          {loading ? (
            <TableLoader />
          ) : (
            <ActivityLibraryTable
              activities={activities}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          )}
        </Box>
      </Box>

      <SupportMethodWizard
        opened={defaultPromptOpen}
        forDefault
        defaultSupports={defaultSupports}
        onClose={closeDefaultPrompt}
        onFinishDefault={(picks) => saveDefaultPrompting(picks, closeDefaultPrompt)}
      />
    </Box>
  );
};

export default ActivityLibrary;
